import { Checkbox, DialogActions, DialogContent, FormControlLabel, DialogContentText, FormHelperText, Link } from "@material-ui/core";
import React from "react";
import { DialogContentErrorText } from "../../../../functions/errorHandling";
import IIntegrationSecuritySettings from "../../../../models/IIntegrationSecuritySettings";
import { IIntegrationSecuritySettingsUpdate, SkywardIntegrationTokenActions } from "../../../../models/IIntegrationSecuritySettingsUpdate";
import ISkywardIntegrationTokenPair from "../../../../models/ISkywardIntegrationTokenPair";
import IIntegrationRepository from "../../../../services/IIntegrationRepository";
import Services from "../../../../services/Services";
import WithLoadingSpinners from "../../../hoc/WithLoadingSpinner";
import { RawLinkButton } from "../../../LinkButton";
import { TokenActionSelect } from "./TokenActionSelect";
import getUrl from "../../../../functions/getUrl";
import snackbarService from "../../../../services/ui-messaging/SnackbarService";

export interface ISecuritySettingsDisplayProps {
  token: string;
  id: number;
  onClose: () => void;
  onSetTokenResult: (pair: ISkywardIntegrationTokenPair) => void;
}

interface ISecuritySettingsDisplayModel {
  Settings: IIntegrationSecuritySettings;
  Update: IIntegrationSecuritySettingsUpdate;
}

let repo: IIntegrationRepository | null = null;

export const SecuritySettingsDisplay = (props: ISecuritySettingsDisplayProps): JSX.Element => {
  const { token, id, onClose, onSetTokenResult } = props;
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [model, setModel] = React.useState<ISecuritySettingsDisplayModel | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const getDefaultAction = (allowCustomerToSetSecret: boolean, hasToken: boolean): SkywardIntegrationTokenActions =>
    !allowCustomerToSetSecret && hasToken ? SkywardIntegrationTokenActions.Revoke : SkywardIntegrationTokenActions.None;

  const getDefaultUpdate = (settings: IIntegrationSecuritySettings): IIntegrationSecuritySettingsUpdate => {
    return {
      AllowCustomerToSetSecret: settings.AllowCustomerToSetSecret,
      SkywardIntegrationToken1Action: getDefaultAction(settings.AllowCustomerToSetSecret, settings.HasSkywardIntegrationToken1),
      SkywardIntegrationToken2Action: getDefaultAction(settings.AllowCustomerToSetSecret, settings.HasSkywardIntegrationToken2),
    };
  };

  const getSettings = async () => {
    setIsLoading(true);

    try {
      if (repo === null) {
        repo = await new Services().IntegrationRepository();
      }

      const settings = await repo.getIntegrationSecuritySettings(token, id);
      setModel({
        Settings: settings,
        Update: getDefaultUpdate(settings),
      });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Unknown error");
      }
    }

    setIsLoading(false);
  };

  React.useEffect(() => {
    getSettings();
  }, []);

  const hasChanges =
    model !== null &&
    (model.Update.AllowCustomerToSetSecret !== model.Settings.AllowCustomerToSetSecret ||
      model.Update.SkywardIntegrationToken1Action !== SkywardIntegrationTokenActions.None ||
      model.Update.SkywardIntegrationToken2Action !== SkywardIntegrationTokenActions.None);

  const handleAllowCustomerToSetSecretChange = (isChecked: boolean) => {
    if (model === null) {
      return;
    }

    setModel({
      Settings: model.Settings,
      Update: {
        AllowCustomerToSetSecret: isChecked,
        SkywardIntegrationToken1Action: isChecked
          ? SkywardIntegrationTokenActions.None
          : getDefaultAction(isChecked, model.Settings.HasSkywardIntegrationToken1),
        SkywardIntegrationToken2Action: isChecked
          ? SkywardIntegrationTokenActions.None
          : getDefaultAction(isChecked, model.Settings.HasSkywardIntegrationToken2),
      },
    });
  };

  const handleTokenActionChange = (fieldName: "SkywardIntegrationToken1Action" | "SkywardIntegrationToken2Action", action: SkywardIntegrationTokenActions) => {
    if (model === null) {
      return;
    }

    setModel({
      Settings: model.Settings,
      Update: {
        ...model.Update,
        [fieldName]: action,
      },
    });
  };

  const onSubmit = () => {
    setError(null);
    setIsSubmitting(true);
    submitAsync();
  };

  const submitAsync = async () => {
    if (repo === null || model === null) {
      setIsSubmitting(false);
      return;
    }

    try {
      const pair: ISkywardIntegrationTokenPair = await repo.setIntegrationSecuritySettings(token, id, model.Update);

      await getSettings();
      onSetTokenResult(pair);
      snackbarService.success("Successfully updated Security Settings");
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Unknown error");
      }
    }

    setIsSubmitting(false);
  };

  return (
    <WithLoadingSpinners message="Fetching Security Settings" isLoading={isLoading}>
      {model && (
        <>
          <DialogContent>
            <FormControlLabel
              control={<Checkbox checked={model.Update.AllowCustomerToSetSecret} onChange={(_, checked) => handleAllowCustomerToSetSecretChange(checked)} />}
              label="Allow Customer to Set Secret"
            />
            <FormHelperText>Only check this box if this integration's credentials will be put into your system by a Qmlativ user.</FormHelperText>
            <FormHelperText>
              There are extra security concerns when customers can generate their own secrets.{" "}
              <Link href={getUrl("/docs?id=qmlativ%2fauthentication.html")}>Qmlativ Authentication Documentation</Link>
            </FormHelperText>
          </DialogContent>
          <DialogContent>
            <DialogContentText>Skyward Integration Tokens</DialogContentText>
            <TokenActionSelect
              label="Token 1"
              value={model.Update.SkywardIntegrationToken1Action}
              disabled={!model.Update.AllowCustomerToSetSecret}
              hasToken={model.Settings.HasSkywardIntegrationToken1}
              tokenHint={model.Settings.SkywardIntegrationToken1Hint}
              onChange={value => handleTokenActionChange("SkywardIntegrationToken1Action", value)}
            />
          </DialogContent>
          <DialogContent>
            <TokenActionSelect
              label="Token 2"
              value={model.Update.SkywardIntegrationToken2Action}
              disabled={!model.Update.AllowCustomerToSetSecret}
              hasToken={model.Settings.HasSkywardIntegrationToken2}
              tokenHint={model.Settings.SkywardIntegrationToken2Hint}
              onChange={value => handleTokenActionChange("SkywardIntegrationToken2Action", value)}
            />
          </DialogContent>
        </>
      )}
      {error && (
        <>
          <DialogContent>
            <DialogContentErrorText>{error}</DialogContentErrorText>
          </DialogContent>
        </>
      )}
      <DialogActions>
        {model && (
          <RawLinkButton onClick={onSubmit} disabled={!hasChanges || isSubmitting}>
            Submit
          </RawLinkButton>
        )}
        <RawLinkButton onClick={onClose}>Close</RawLinkButton>
      </DialogActions>
    </WithLoadingSpinners>
  );
};
